import { useState } from "react";
import WorkshopEnquiryModal from "./WorkshopEnquiryModal";

export default function InnovationWorkshops() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedWorkshop, setSelectedWorkshop] = useState("");

  const openModal = (title: string) => {
    setSelectedWorkshop(title);
    setModalOpen(true);
  };

  const workshops = [
    {
      title: 'Robotics Hackathon',
      duration: '3 Days',
      grades: 'Grades 8-12',
      participants: '30-50 students',
      image: 'https://readdy.ai/api/search-image?query=Students%20participating%20in%20robotics%20hackathon%20competition%2C%20teams%20working%20on%20robot%20projects%2C%20energetic%20workshop%20atmosphere%20with%20colorful%20displays%2C%20modern%20classroom%20with%20technology%20equipment&width=600&height=400&seq=workshop-robotics-001&orientation=landscape',
      outcomes: [
        'Build functional robots',
        'Team collaboration skills',
        'Problem-solving abilities'
      ],
      color: 'from-blue-600 to-blue-500'
    },
    {
      title: 'AI Innovation Challenge',
      duration: '2 Days',
      grades: 'Grades 9-12',
      participants: '25-40 students',
      image: 'https://readdy.ai/api/search-image?query=AI%20innovation%20workshop%20with%20students%20working%20on%20machine%20learning%20projects%2C%20laptops%20showing%20code%20and%20data%20visualizations%2C%20collaborative%20learning%20environment%20with%20modern%20technology&width=600&height=400&seq=workshop-ai-001&orientation=landscape',
      outcomes: [
        'AI model development',
        'Data analysis skills',
        'Presentation abilities'
      ],
      color: 'from-purple-600 to-purple-500'
    },
    {
      title: 'IoT Maker Workshop',
      duration: '2 Days',
      grades: 'Grades 7-11',
      participants: '20-35 students',
      image: 'https://readdy.ai/api/search-image?query=IoT%20maker%20workshop%20with%20students%20building%20smart%20devices%2C%20electronic%20components%20and%20sensors%20on%20tables%2C%20hands-on%20learning%20with%20microcontrollers%2C%20creative%20technology%20classroom&width=600&height=400&seq=workshop-iot-001&orientation=landscape',
      outcomes: [
        'Smart device creation',
        'Circuit design skills',
        'Innovation mindset'
      ],
      color: 'from-green-600 to-green-500'
    },
    {
      title: 'Design Thinking Sprint',
      duration: '1 Day',
      grades: 'Grades 6-10',
      participants: '30-50 students',
      image: 'https://readdy.ai/api/search-image?query=Design%20thinking%20workshop%20with%20students%20brainstorming%20ideas%2C%20colorful%20sticky%20notes%20on%20walls%2C%20creative%20problem%20solving%20session%2C%20collaborative%20team%20activities%20in%20bright%20classroom&width=600&height=400&seq=workshop-design-001&orientation=landscape',
      outcomes: [
        'Creative problem-solving',
        'User-centric design',
        'Rapid prototyping'
      ],
      color: 'from-orange-600 to-orange-500'
    }
  ];

  return (
    <section id="workshops" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-orange-100 text-orange-600 text-sm font-semibold uppercase tracking-wider rounded-full mb-4">
            Hands-On Learning
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Innovation Workshops &amp; Programs
          </h2>
          <p className="text-base text-gray-600 max-w-3xl mx-auto">
            Intensive bootcamps and hackathons that foster creativity, teamwork, and real-world problem-solving skills
          </p>
        </div>

        {/* Workshops Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {workshops.map((workshop, idx) => (
            <div
              key={idx}
              className="bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all border border-gray-200"
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={workshop.image}
                  alt={workshop.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className={`absolute top-4 left-4 px-4 py-2 bg-gradient-to-r ${workshop.color} text-white font-bold rounded-full text-sm`}>
                  {workshop.duration}
                </div>
              </div>

              <div className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{workshop.title}</h3>
                
                <div className="flex flex-wrap gap-3 mb-6">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-medium rounded-full">
                    {workshop.grades}
                  </span>
                  <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                    {workshop.participants}
                  </span>
                </div>

                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Key Outcomes:</h4>
                  <ul className="space-y-2">
                    {workshop.outcomes.map((outcome, oIdx) => (
                      <li key={oIdx} className="flex items-center gap-2 text-gray-700">
                        <i className="ri-arrow-right-s-line text-orange-500"></i>
                        {outcome}
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => openModal(workshop.title)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-calendar-schedule-line text-base"></i>
                  Schedule Workshop
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Workshop Benefits */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-3xl p-12">
          <h3 className="text-2xl font-bold text-gray-900 text-center mb-10">
            Why Choose Our Workshops
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { icon: 'ri-team-line', title: 'Expert Mentors', desc: 'Industry professionals and educators' },
              { icon: 'ri-trophy-line', title: 'Competition Ready', desc: 'Prepare for national competitions' },
              { icon: 'ri-award-line', title: 'Certificates', desc: 'Participation and achievement awards' },
              { icon: 'ri-global-line', title: 'Global Exposure', desc: 'Connect with international programs' }
            ].map((benefit, idx) => (
              <div key={idx} className="text-center">
                <div className="w-14 h-14 mx-auto bg-gradient-to-br from-blue-600 to-blue-400 rounded-xl flex items-center justify-center mb-3 shadow-lg">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h4 className="text-base font-bold text-gray-900 mb-2">{benefit.title}</h4>
                <p className="text-gray-600 text-sm">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <WorkshopEnquiryModal
        isOpen={modalOpen}
        workshopTitle={selectedWorkshop}
        onClose={() => setModalOpen(false)}
      />
    </section>
  );
}
